// Landing page DocuSign redirects to after an embedded signing session ends.
// DocuSign appends an `event` query param — we branch on that to show the
// right message. We also emit a `postMessage` beacon so the parent tab
// (usually the portal Documents view still open in the background) can
// re-fetch the doc status the next time it polls, no page-refresh needed.
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, AlertTriangle, ArrowLeft } from "lucide-react";
import { portal } from "../../lib/api";

// DocuSign returns one of these events on the returnUrl. Map each to a
// friendly headline, subheadline, and status color.
const EVENT_MAP = {
  signing_complete:  { icon: CheckCircle2, tone: "emerald", title: "You're all set — thank you!", subtitle: "Your signature was captured. Your agent has been notified automatically." },
  viewing_complete:  { icon: CheckCircle2, tone: "emerald", title: "Signed and confirmed", subtitle: "Your signature is in — safe to close this tab." },
  cancel:            { icon: XCircle,      tone: "slate",   title: "Signing cancelled", subtitle: "No changes were saved. You can reopen the signing session any time from your portal." },
  decline:           { icon: XCircle,      tone: "rose",    title: "Signing declined", subtitle: "You declined to sign. Your agent will follow up shortly." },
  session_timeout:   { icon: Clock,        tone: "amber",   title: "Session timed out", subtitle: "DocuSign sessions are single-use and expire quickly. Head back to your portal and open a fresh signing session." },
  ttl_expired:       { icon: Clock,        tone: "amber",   title: "Signing link expired", subtitle: "The link that got you here was one-time use. Return to your portal to open a new signing session." },
  exception:         { icon: AlertTriangle,tone: "rose",    title: "Something went wrong", subtitle: "DocuSign reported an error. Your agent has been notified — please try again from your portal." },
  fax_pending:       { icon: Clock,        tone: "amber",   title: "Fax pending", subtitle: "DocuSign is waiting for a fax signature to complete this envelope." },
  access_code_failed:{ icon: XCircle,      tone: "rose",    title: "Access code failed", subtitle: "The access code didn't match. Please try again from your portal." },
  id_check_failed:   { icon: XCircle,      tone: "rose",    title: "ID check failed", subtitle: "DocuSign couldn't verify your ID. Contact your agent to try a different signing method." },
};

const TONE = {
  emerald: { bar: "bg-emerald-500", ring: "ring-emerald-100", chipBg: "bg-emerald-50", chipTxt: "text-emerald-900", icon: "text-emerald-600" },
  amber:   { bar: "bg-amber-500",   ring: "ring-amber-100",   chipBg: "bg-amber-50",   chipTxt: "text-amber-900",   icon: "text-amber-600" },
  rose:    { bar: "bg-rose-500",    ring: "ring-rose-100",    chipBg: "bg-rose-50",    chipTxt: "text-rose-900",    icon: "text-rose-600" },
  slate:   { bar: "bg-slate-500",   ring: "ring-slate-100",   chipBg: "bg-slate-50",   chipTxt: "text-slate-900",   icon: "text-slate-600" },
};

export default function PortalSignComplete() {
  const [params] = useSearchParams();
  const event = (params.get("event") || "").toLowerCase();
  const docId = params.get("doc_id") || null;

  const meta = useMemo(() => EVENT_MAP[event] || {
    icon: CheckCircle2, tone: "emerald",
    title: "Signing session closed",
    subtitle: "You can safely return to your portal.",
  }, [event]);
  const tone = TONE[meta.tone] || TONE.slate;
  const Icon = meta.icon;

  // Ping the opener (new-tab flow) AND the parent frame (in-portal
  // iframe flow) so the portal's Documents tab knows to re-fetch and
  // show the latest signature_status. Also POST the event to the
  // backend so the agent sees it in the Signing activity log.
  useEffect(() => {
    const msg = { type: "docusign_signed", docId, event };
    // Target origin: only send to same-origin frames/openers. Both the
    // agent CRM and the portal live on the same origin as this signing
    // callback page, so `window.location.origin` is the tightest valid
    // target. Anything else would let a malicious host that opened us
    // in an iframe read the signed doc id — closes the postMessage
    // "*" wildcard security gap flagged in the code review.
    const targetOrigin = window.location.origin;
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(msg, targetOrigin);
      }
    } catch (e) { console.debug("postMessage to opener failed", e); }
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(msg, targetOrigin);
      }
    } catch (e) { console.debug("postMessage to parent failed", e); }
    if (docId && event) {
      portal.post(`/portal/documents/${docId}/signing-events`, { event })
        .catch((e) => console.debug("signing-event log failed (portal token may have expired)", e?.response?.status));
    }
  }, [docId, event]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className={`max-w-md w-full rounded-2xl bg-white shadow-lg overflow-hidden ring-1 ${tone.ring}`} data-testid="portal-sign-complete">
        <div className={`h-1.5 w-full ${tone.bar}`} />
        <div className="p-8 text-center">
          <div className={`h-14 w-14 mx-auto rounded-full ${tone.chipBg} flex items-center justify-center`}>
            <Icon className={`h-7 w-7 ${tone.icon}`} />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900" data-testid="sign-complete-title">
            {meta.title}
          </h1>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">{meta.subtitle}</p>
          {event && (
            <div className={`mt-4 inline-flex items-center gap-2 rounded-full ${tone.chipBg} ${tone.chipTxt} text-[10px] font-semibold uppercase tracking-widest px-3 py-1`} data-testid="sign-complete-event">
              DocuSign · {event.replace(/_/g, " ")}
            </div>
          )}
          <div className="mt-8 flex items-center justify-center gap-2">
            <Link
              to="/portal"
              className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              data-testid="sign-complete-back-btn"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to your portal
            </Link>
          </div>
          <button
            onClick={() => window.close()}
            className="mt-3 text-[11px] text-slate-400 hover:text-slate-700"
            data-testid="sign-complete-close-btn"
          >
            Or close this tab
          </button>
        </div>
      </div>
    </div>
  );
}
